"use server";

import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import { verifyPassword } from "@/lib/auth/password";
import {
  clearPlatformSession,
  getPlatformSession,
  setPlatformSession,
} from "@/lib/auth/cookies";
import { unscopedPrisma } from "@/lib/db";
import { createTenantWithAdmin, createTenantAdmin, updateTenantAdminPassword, setTenantSuspended } from "@/lib/platform/tenants";
import {
  parseCreateTenantInput,
  parseManualWalletInput,
  parseTenantAdminInput,
  parseTenantAdminPassword,
  platformCopy,
} from "@/lib/ui/platform";
import { applyManualWalletChange } from "@/lib/platform/wallet";

export type PlatformLoginState = {
  error: string | null;
};

export type CreateTenantState = {
  error: string | null;
  created: string | null;
};

export async function platformLoginAction(
  _prev: PlatformLoginState,
  formData: FormData,
): Promise<PlatformLoginState> {
  const email = String(formData.get("email") ?? "")
    .trim()
    .toLowerCase();
  const password = String(formData.get("password") ?? "");

  if (!email || !password) {
    return { error: "Введите email и пароль" };
  }

  const admin = await unscopedPrisma.platformAdmin.findUnique({
    where: { email },
  });
  if (!admin || !(await verifyPassword(password, admin.passwordHash))) {
    return { error: "Неверный email или пароль" };
  }

  await setPlatformSession({
    adminId: admin.id,
    email: admin.email,
    name: admin.name,
  });
  redirect("/platform");
}

export async function platformLogoutAction(): Promise<void> {
  await clearPlatformSession();
  redirect("/platform/login");
}

export async function createTenantAction(
  _prev: CreateTenantState,
  formData: FormData,
): Promise<CreateTenantState> {
  const session = await getPlatformSession();
  if (!session) {
    return { error: platformCopy.denied, created: null };
  }

  const parsed = parseCreateTenantInput({
    name: String(formData.get("name") ?? ""),
    slug: String(formData.get("slug") ?? ""),
    adminName: String(formData.get("adminName") ?? ""),
    adminEmail: String(formData.get("adminEmail") ?? ""),
    adminPassword: String(formData.get("adminPassword") ?? ""),
  });
  if (!parsed.ok) {
    return { error: parsed.error, created: null };
  }

  const result = await createTenantWithAdmin(parsed.value);
  if (!result.ok) {
    return { error: result.error, created: null };
  }

  revalidatePath("/platform");
  revalidatePath("/platform/tenants");
  return { error: null, created: parsed.value.slug };
}

export type WalletFormState = {
  error: string | null;
  saved: boolean;
};

export async function applyWalletAction(
  tenantId: string,
  _prev: WalletFormState,
  formData: FormData,
): Promise<WalletFormState> {
  const session = await getPlatformSession();
  if (!session) {
    return { error: platformCopy.denied, saved: false };
  }

  const parsed = parseManualWalletInput({
    kind: String(formData.get("kind") ?? ""),
    currency: String(formData.get("currency") ?? ""),
    amount: String(formData.get("amount") ?? ""),
    comment: String(formData.get("comment") ?? ""),
  });
  if (!parsed.ok) {
    return { error: parsed.error, saved: false };
  }

  const result = await applyManualWalletChange({
    tenantId,
    adminId: session.adminId,
    input: parsed.value,
  });
  if (!result.ok) {
    return { error: result.error, saved: false };
  }

  revalidatePath("/platform");
  revalidatePath("/platform/tenants");
  revalidatePath(`/platform/tenants/${tenantId}`);
  revalidatePath(`/platform/tenants/${tenantId}/spend`);
  revalidatePath(`/platform/tenants/${tenantId}/owner`);
  return { error: null, saved: true };
}

export type TenantAdminFormState = {
  error: string | null;
  saved: boolean;
};

function revalidateTenantAdmins(tenantId: string) {
  revalidatePath(`/platform/tenants/${tenantId}`);
  revalidatePath(`/platform/tenants/${tenantId}/owner`);
}

export async function createTenantAdminAction(
  tenantId: string,
  _prev: TenantAdminFormState,
  formData: FormData,
): Promise<TenantAdminFormState> {
  const session = await getPlatformSession();
  if (!session) {
    return { error: platformCopy.denied, saved: false };
  }

  const parsed = parseTenantAdminInput({
    name: String(formData.get("name") ?? ""),
    email: String(formData.get("email") ?? ""),
    password: String(formData.get("password") ?? ""),
  });
  if (!parsed.ok) {
    return { error: parsed.error, saved: false };
  }

  const result = await createTenantAdmin({
    tenantId,
    name: parsed.value.name,
    email: parsed.value.email,
    password: parsed.value.password,
  });
  if (!result.ok) {
    return { error: result.error, saved: false };
  }

  revalidateTenantAdmins(tenantId);
  return { error: null, saved: true };
}

export async function updateTenantAdminPasswordAction(
  tenantId: string,
  userId: string,
  _prev: TenantAdminFormState,
  formData: FormData,
): Promise<TenantAdminFormState> {
  const session = await getPlatformSession();
  if (!session) {
    return { error: platformCopy.denied, saved: false };
  }

  const parsed = parseTenantAdminPassword(String(formData.get("password") ?? ""));
  if (!parsed.ok) {
    return { error: parsed.error, saved: false };
  }

  const result = await updateTenantAdminPassword({
    tenantId,
    userId,
    password: parsed.value,
  });
  if (!result.ok) {
    return { error: result.error, saved: false };
  }

  revalidateTenantAdmins(tenantId);
  return { error: null, saved: true };
}

export async function setTenantSuspendedAction(
  tenantId: string,
  suspended: boolean,
): Promise<{ ok: boolean; error: string | null }> {
  const session = await getPlatformSession();
  if (!session) {
    return { ok: false, error: platformCopy.denied };
  }
  const result = await setTenantSuspended({ tenantId, suspended });
  if (!result.ok) {
    return { ok: false, error: result.error };
  }
  revalidatePath("/platform");
  revalidatePath("/platform/tenants");
  revalidatePath("/platform/ops");
  revalidatePath("/platform/ops/journal");
  revalidatePath(`/platform/tenants/${tenantId}`);
  revalidatePath(`/platform/tenants/${tenantId}/spend`);
  revalidatePath(`/platform/tenants/${tenantId}/owner`);
  revalidatePath(`/platform/tenants/${tenantId}/access`);
  return { ok: true, error: null };
}
